/**
 * Phase 3u — Dev Mode Catalog tab.
 *
 * Shows every voice Meridian has silently remembered across sessions
 * via the `unknown_voices` catalog. Each row carries a stable
 * colored identity ("Speaker A", "Speaker B", ...) so voices feel
 * like distinct people rather than anonymous IDs — the catalog
 * reads like a contact list of unnamed acquaintances.
 *
 * Used to verify Signal C (recurring voices should show
 * occurrenceCount ≥ 2 AND carry the same display label across
 * sessions) and to see which voices Meridian is quietly
 * remembering without nagging the user.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Loader2, RefreshCw, Sparkles } from "lucide-react";

import { useContacts } from "@/hooks/useContacts";
import { devListUnknownVoices } from "@/hooks/useDevDiagnostics";
import type { Contact } from "@/lib/data";
import { getFullName } from "@/lib/data";
import type { DevUnknownVoice } from "@/types/devMode";

import { CatalogDetailSheet } from "./CatalogDetailSheet";
import { formatRelativeTime } from "./devFormatters";

/**
 * Resolve the display title for a catalog voice. Once the voice has
 * been named (user_self via "This is me", or a contact via "Name this
 * voice"), the card reads under the resolved identity so the Catalog
 * reflects the user's mental model, not the auto-assigned Speaker A/B
 * shorthand. Falls back to the generic label for unresolved voices.
 */
function resolvedVoiceTitle(
  voice: DevUnknownVoice,
  contacts: Contact[]
): string {
  if (voice.resolvedContactId === "user_self") return "You";
  if (voice.resolvedContactId) {
    const contact = contacts.find(
      (c) =>
        c.appleContactId === voice.resolvedContactId ||
        c.id === voice.resolvedContactId
    );
    if (contact) return getFullName(contact) || "Unnamed contact";
  }
  return voice.displayLabel ?? "Unlabeled";
}

export function CatalogTab() {
  const [voices, setVoices] = useState<DevUnknownVoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<DevUnknownVoice | null>(null);
  const { contacts } = useContacts();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const rows = await devListUnknownVoices();
      setVoices(rows);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) {
    return <p className="p-4 text-sm text-red-400">Failed to load: {error}</p>;
  }

  if (!voices) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-white/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading catalog…
      </div>
    );
  }

  if (voices.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.04]">
          <Sparkles className="h-5 w-5 text-white/30" />
        </div>
        <p className="text-sm text-white/60">No voices catalogued yet.</p>
        <p className="text-[11px] leading-relaxed text-white/40">
          Unknown voices land here after a session is processed with the
          frame classifier. Recurring voices pick up occurrence counts;
          first-encounters stay silent unless a significance signal fires.
        </p>
      </div>
    );
  }

  const recurring = voices.filter((v) => v.occurrenceCount >= 2).length;
  const promoted = voices.filter((v) => v.promotedAt !== null).length;
  const resolved = voices.filter((v) => v.resolvedContactId !== null).length;

  return (
    <div className="flex flex-col gap-3 p-4">
      <header className="flex items-end justify-between gap-3">
        <div className="flex flex-col">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/30">
            Silent memory
          </p>
          <p className="mt-0.5 text-[22px] font-semibold leading-none text-white">
            {voices.length}
            <span className="ml-1.5 text-[12px] font-normal text-white/40">
              {voices.length === 1 ? "voice" : "voices"}
            </span>
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
            {recurring > 0 && (
              <Chip tint="violet" label={`${recurring} recurring`} />
            )}
            {promoted > 0 && (
              <Chip tint="amber" label={`${promoted} prompted`} />
            )}
            {resolved > 0 && (
              <Chip tint="emerald" label={`${resolved} named`} />
            )}
          </div>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-white/60 active:opacity-60"
        >
          <RefreshCw className="h-3 w-3" />
          refresh
        </button>
      </header>

      <div className="flex flex-col gap-2">
        {voices.map((voice) => (
          <VoiceCard
            key={voice.id}
            voice={voice}
            contacts={contacts}
            onOpen={() => setActive(voice)}
          />
        ))}
      </div>

      {active && (
        <CatalogDetailSheet
          voice={active}
          onClose={() => setActive(null)}
          onResolved={() => {
            setActive(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function VoiceCard({
  voice,
  contacts,
  onOpen,
}: {
  voice: DevUnknownVoice;
  contacts: Contact[];
  onOpen: () => void;
}) {
  const isRecurring = voice.occurrenceCount >= 2;
  const isPromoted = voice.promotedAt !== null;
  const isResolved = voice.resolvedContactId !== null;

  const status = resolveStatus({ isRecurring, isPromoted, isResolved });
  const identity = identityFor(voice.displayLabel ?? voice.id);
  const title = resolvedVoiceTitle(voice, contacts);

  const storyline = buildStoryline(voice);
  const speechTime = humanDuration(voice.totalSpeechSeconds);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative w-full overflow-hidden rounded-xl border p-3 text-left transition-transform active:scale-[0.995]"
      style={{
        borderColor: status.borderColor,
        backgroundColor: status.backgroundColor,
      }}
    >
      {/* Subtle accent glow in the corner behind the avatar to give
          each card a warm identity instead of flat dev-tool rows. */}
      <div
        className="pointer-events-none absolute -left-6 -top-6 h-24 w-24 rounded-full blur-2xl"
        style={{ backgroundColor: identity.glow }}
      />

      <div className="relative flex items-start gap-3">
        <Avatar identity={identity} displayLabel={voice.displayLabel} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-[15px] font-semibold text-white">
                {title}
              </span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/30" />
            </span>
            <StatusPill status={status} />
          </div>
          {isResolved && voice.displayLabel && voice.displayLabel !== title && (
            <p className="mt-0.5 text-[11px] text-white/40">
              was {voice.displayLabel}
            </p>
          )}
          <p className="mt-0.5 font-mono text-[9px] tracking-tight text-white/25">
            {voice.id.slice(0, 8)}
          </p>
          <p className="mt-2 text-[12px] leading-snug text-white/70">
            {storyline}
          </p>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-white/40">
            <span>{speechTime} of speech</span>
            <span className="h-[3px] w-[3px] rounded-full bg-white/20" />
            <span>last {formatRelativeTime(voice.lastSeenAt)}</span>
          </div>
        </div>
      </div>

      {isPromoted && (
        <p className="relative mt-2 rounded-md bg-black/20 px-2 py-1 text-[10px] italic text-amber-100/60">
          Already prompted — debounced from re-asking.
        </p>
      )}
      {isResolved && voice.resolvedContactId && (
        <p className="relative mt-2 rounded-md bg-black/20 px-2 py-1 text-[10px] italic text-emerald-100/70">
          {voice.resolvedContactId === "user_self"
            ? "Merged into your voiceprint."
            : `Named as ${title}.`}
        </p>
      )}
    </button>
  );
}

// MARK: - Avatar + identity

interface Identity {
  letter: string;
  hue: number;
  ring: string;
  fill: string;
  glow: string;
}

function identityFor(seed: string): Identity {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) & 0xffffffff;
  }
  const hue = Math.abs(hash) % 360;
  // Pull the last letter from a "Speaker X" label so "Speaker AA"
  // would render with two letters still, but anything else starts
  // from the first alpha char. Falls back to "?" for truly empty.
  const match = seed.match(/([A-Z]+)$/i);
  const letter = (match ? match[1] : seed[0] ?? "?").slice(0, 2).toUpperCase();
  return {
    letter,
    hue,
    ring: `hsla(${hue}, 70%, 65%, 0.45)`,
    fill: `hsla(${hue}, 55%, 22%, 1)`,
    glow: `hsla(${hue}, 70%, 45%, 0.12)`,
  };
}

function Avatar({
  identity,
  displayLabel,
}: {
  identity: Identity;
  displayLabel: string | null;
}) {
  return (
    <div
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-[13px] font-semibold tracking-tight text-white"
      style={{
        borderColor: identity.ring,
        backgroundColor: identity.fill,
        textShadow: "0 0 8px rgba(0,0,0,0.4)",
      }}
      aria-label={displayLabel ?? "Unknown voice"}
    >
      {identity.letter}
    </div>
  );
}

// MARK: - Status styling

type StatusKind = "resolved" | "promoted" | "recurring" | "silent";

interface StatusDescriptor {
  kind: StatusKind;
  label: string;
  borderColor: string;
  backgroundColor: string;
  chipBg: string;
  chipFg: string;
}

function resolveStatus(input: {
  isRecurring: boolean;
  isPromoted: boolean;
  isResolved: boolean;
}): StatusDescriptor {
  if (input.isResolved) {
    return {
      kind: "resolved",
      label: "named",
      borderColor: "rgba(52, 211, 153, 0.35)",
      backgroundColor: "rgba(16, 185, 129, 0.06)",
      chipBg: "rgba(16, 185, 129, 0.2)",
      chipFg: "rgb(167, 243, 208)",
    };
  }
  if (input.isPromoted) {
    return {
      kind: "promoted",
      label: "asked",
      borderColor: "rgba(251, 191, 36, 0.35)",
      backgroundColor: "rgba(251, 191, 36, 0.05)",
      chipBg: "rgba(251, 191, 36, 0.2)",
      chipFg: "rgb(253, 230, 138)",
    };
  }
  if (input.isRecurring) {
    return {
      kind: "recurring",
      label: "recurring",
      borderColor: "rgba(167, 139, 250, 0.35)",
      backgroundColor: "rgba(139, 92, 246, 0.05)",
      chipBg: "rgba(139, 92, 246, 0.2)",
      chipFg: "rgb(221, 214, 254)",
    };
  }
  return {
    kind: "silent",
    label: "remembered",
    borderColor: "rgba(255, 255, 255, 0.08)",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    chipBg: "rgba(255, 255, 255, 0.08)",
    chipFg: "rgba(255, 255, 255, 0.55)",
  };
}

function StatusPill({ status }: { status: StatusDescriptor }) {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em]"
      style={{ backgroundColor: status.chipBg, color: status.chipFg }}
    >
      {status.label}
    </span>
  );
}

function Chip({
  tint,
  label,
}: {
  tint: "amber" | "violet" | "emerald";
  label: string;
}) {
  const palette = {
    amber: { bg: "rgba(251, 191, 36, 0.14)", fg: "rgb(253, 230, 138)" },
    violet: { bg: "rgba(139, 92, 246, 0.14)", fg: "rgb(221, 214, 254)" },
    emerald: { bg: "rgba(16, 185, 129, 0.14)", fg: "rgb(167, 243, 208)" },
  }[tint];
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    >
      {label}
    </span>
  );
}

// MARK: - Human storytelling

function buildStoryline(voice: DevUnknownVoice): string {
  const occurrences = voice.occurrenceCount;
  const span = relativeSpan(voice.firstSeenAt, voice.lastSeenAt);
  if (occurrences <= 1) {
    return `Heard once, ${formatRelativeTime(voice.firstSeenAt)}.`;
  }
  if (span === null) {
    return `Heard ${occurrences}×.`;
  }
  return `Heard ${occurrences}× across ${span}.`;
}

function relativeSpan(firstIso: string, lastIso: string): string | null {
  const first = new Date(firstIso).getTime();
  const last = new Date(lastIso).getTime();
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  const deltaMs = Math.max(0, last - first);
  if (deltaMs < 60_000) return `${Math.round(deltaMs / 1000)}s`;
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

function humanDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds - minutes * 60);
  return remSeconds === 0 ? `${minutes}m` : `${minutes}m ${remSeconds}s`;
}
